/* ============================================
   Apps · Activaciones (admin)
   Alta y gestión del "derecho de acceso" de un cliente a una app.
   Genera un código que el cliente canjea en el gateway (raglink).
   Endpoints: /api/apps/activaciones (GET/POST/PUT/DELETE, adminOnly).
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { AppActivacion, AppCatalogo, EstadoActivacion } from '@/lib/types'

interface ClienteMin { id: number; empresa: string }

const ESTADO_BADGE: Record<EstadoActivacion, { cls: string; label: string }> = {
  activada: { cls: 'badge-activo', label: 'Activada' },
  pendiente: { cls: 'badge-pausado', label: 'Pendiente' },
  revocada: { cls: 'badge-cancelado', label: 'Revocada' },
}

function fmt(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) } catch { return '—' }
}

export default function AppsActivaciones() {
  const api = useApi()
  const [activaciones, setActivaciones] = useState<AppActivacion[]>([])
  const [clientes, setClientes] = useState<ClienteMin[]>([])
  const [apps, setApps] = useState<AppCatalogo[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [clienteId, setClienteId] = useState('')
  const [appSlug, setAppSlug] = useState('')
  const [asientos, setAsientos] = useState('1')

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const [act, cli, ap] = await Promise.all([
        api.get<AppActivacion[]>('/api/apps/activaciones').catch(() => []),
        api.get<ClienteMin[]>('/api/clientes').catch(() => []),
        api.get<AppCatalogo[]>('/api/apps').catch(() => []),
      ])
      setActivaciones(Array.isArray(act) ? act : [])
      setClientes((Array.isArray(cli) ? cli : []).map(c => ({ id: c.id, empresa: c.empresa })))
      setApps(Array.isArray(ap) ? ap : [])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { cargar() }, [cargar])

  async function crear(e: React.FormEvent) {
    e.preventDefault()
    if (!clienteId || !appSlug) { showToast('Elegí cliente y app', 'error'); return }
    setSaving(true)
    try {
      await api.post('/api/apps/activaciones', {
        cliente_id: Number(clienteId),
        app_slug: appSlug,
        asientos: Number(asientos) || 1,
      })
      showToast('Activación creada. Pasale el código al cliente.', 'success')
      setClienteId(''); setAppSlug(''); setAsientos('1')
      cargar()
    } catch {
      showToast('No se pudo crear la activación', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function marcarActivada(id: string) {
    try {
      await api.put(`/api/apps/activaciones/${id}`, { estado: 'activada' })
      showToast('Marcada como activada', 'success')
      cargar()
    } catch { showToast('Error al actualizar', 'error') }
  }

  async function revocar(id: string) {
    if (!confirm('¿Revocar esta activación?')) return
    try {
      await api.put(`/api/apps/activaciones/${id}`, { estado: 'revocada' })
      showToast('Activación revocada', 'success')
      cargar()
    } catch { showToast('Error al revocar', 'error') }
  }

  async function eliminar(id: string) {
    if (!confirm('¿Eliminar la activación? Esta acción no se puede deshacer.')) return
    try {
      await api.del(`/api/apps/activaciones/${id}`)
      showToast('Activación eliminada', 'success')
      cargar()
    } catch { showToast('Error al eliminar', 'error') }
  }

  return (
    <div>
      {/* Alta */}
      <form className="card" onSubmit={crear} style={{ padding: 16, marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: '1 1 220px', margin: 0 }}>
          <label className="form-label">Cliente</label>
          <select className="form-input" value={clienteId} onChange={e => setClienteId(e.target.value)}>
            <option value="">Elegir cliente…</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ flex: '1 1 220px', margin: 0 }}>
          <label className="form-label">App</label>
          <select className="form-input" value={appSlug} onChange={e => setAppSlug(e.target.value)}>
            <option value="">Elegir app…</option>
            {apps.map(a => <option key={a.slug} value={a.slug}>{a.nombre}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ flex: '0 0 110px', margin: 0 }}>
          <label className="form-label">Asientos</label>
          <input className="form-input" type="number" min={1} value={asientos} onChange={e => setAsientos(e.target.value)} />
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Creando…' : 'Crear activación'}
        </button>
      </form>

      {/* Tabla */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>App</th>
              <th>Asientos</th>
              <th>Estado</th>
              <th>Código</th>
              <th>Vence</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>Cargando…</td></tr>
            ) : activaciones.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>Sin activaciones todavía. Creá una arriba.</td></tr>
            ) : activaciones.map(a => {
              const badge = ESTADO_BADGE[a.estado] ?? ESTADO_BADGE.pendiente
              return (
                <tr key={a.id}>
                  <td>{a.cliente_nombre || `#${a.cliente_id}`}</td>
                  <td>{apps.find(ap => ap.slug === a.app_slug)?.nombre || a.app_slug}</td>
                  <td>{a.asientos}</td>
                  <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                  <td><code>{a.codigo}</code></td>
                  <td>{fmt(a.codigo_expira_at)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {a.estado === 'pendiente' && (
                      <button className="btn btn-sm btn-secondary" onClick={() => marcarActivada(a.id)} style={{ marginRight: 6 }}>Activada</button>
                    )}
                    {a.estado !== 'revocada' && (
                      <button className="btn btn-sm btn-ghost" onClick={() => revocar(a.id)} style={{ marginRight: 6 }}>Revocar</button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => eliminar(a.id)}>Eliminar</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
